import { FieldType } from '@databases/model/field';
import {
  type CurrentUserValue,
  type MutableUserValue,
  type ReferencedUserValue,
  type SingleUserValue,
  UserType,
  type UserValue,
  type ValueJSON
} from '@databases/model/value';
import * as Y from 'yjs';

import { type GetCurrentUser } from '../default-value-converters.ts';
import { SynchronizedArray } from '../helpers/synchronized-array.ts';
import { SynchronizedMapElement } from '../helpers/synchronized-map-element.ts';
import { SynchronizedRecord } from '../helpers/synchronized-record.ts';
import { type DisposableElement, type WrappedYType } from '../helpers/types.ts';
import { ValueProperties } from '../y-entry.ts';
import { FieldMapping } from '../y-field.ts';

const UserValueProperties = {
  accountId: 'a',
  id: 'i',
  type: 't'
};

enum YUserValueTypes {
  YCurrentUser = 'c',
  YReferencedUser = 'r'
}

const UserValueTypeMap = {
  [UserType.Current]: YUserValueTypes.YCurrentUser,
  [UserType.Referenced]: YUserValueTypes.YReferencedUser
};

class YCurrentUserValue extends SynchronizedRecord implements CurrentUserValue {
  constructor(data: Y.Map<any>) {
    super(data);
  }

  get type(): UserType.Current {
    return UserType.Current;
  }

  toJSON(): SingleUserValue {
    return {
      type: UserType.Current
    };
  }

  static fromJson(value: CurrentUserValue): YCurrentUserValue {
    return new YCurrentUserValue(
      new Y.Map([[UserValueProperties.type, UserValueTypeMap[value.type]]])
    );
  }
}

class YReferencedUserValue extends SynchronizedRecord implements ReferencedUserValue {
  constructor(data: Y.Map<any>) {
    super(data);
  }

  get accountId(): string {
    return this.getString(UserValueProperties.accountId, '');
  }

  set accountId(val: string) {
    this.set(UserValueProperties.accountId, val);
  }

  get id(): string {
    return this.getString(UserValueProperties.id, '');
  }

  get type(): UserType.Referenced {
    return UserType.Referenced;
  }

  toJSON(): SingleUserValue {
    return {
      accountId: this.accountId,
      id: this.id,
      type: UserType.Referenced
    };
  }

  static fromJson(value: ReferencedUserValue): YReferencedUserValue {
    return new YReferencedUserValue(
      new Y.Map([
        [UserValueProperties.accountId, value.accountId],
        [UserValueProperties.id, value.id],
        [UserValueProperties.type, UserValueTypeMap[value.type]]
      ])
    );
  }
}

type YSingleUserValue = YReferencedUserValue | YCurrentUserValue;

export const ySingleUserValueFromJSON = (value: SingleUserValue): YSingleUserValue => {
  switch (value.type) {
    case UserType.Current:
      return YCurrentUserValue.fromJson(value);
    case UserType.Referenced:
      return YReferencedUserValue.fromJson(value);
  }
};

export class YUserValue
  implements UserValue, MutableUserValue, WrappedYType, DisposableElement
{
  private valueElement: SynchronizedMapElement<SynchronizedArray<YSingleUserValue>>;

  constructor(private input: Y.Map<any>) {
    this.valueElement = this.createValueElement();
  }

  private createValueElement() {
    return new SynchronizedMapElement<SynchronizedArray<YSingleUserValue>>(
      this.input,
      ValueProperties.value,
      (yarray) => {
        if (yarray instanceof Y.Array) {
          return new SynchronizedArray(yarray as Y.Array<unknown>, (value: unknown) => {
            if (value instanceof Y.Map) {
              const type = value.get(UserValueProperties.type);
              if (type === YUserValueTypes.YReferencedUser) {
                return new YReferencedUserValue(value);
              } else if (type === YUserValueTypes.YCurrentUser) {
                return new YCurrentUserValue(value);
              }
            }
          });
        }
      },
      () => new Y.Array()
    );
  }

  get isEmpty() {
    return !this.valueElement.value?.toArray().length;
  }

  get type(): FieldType.User {
    return FieldType.User;
  }

  get value() {
    return this.valueElement.value?.toArray() ?? [];
  }

  add(value: SingleUserValue): void {
    this.valueElement.valueRequired.push([ySingleUserValueFromJSON(value)]);
  }

  insert(idx: number, value: SingleUserValue): void {
    this.valueElement.valueRequired.insert(idx, [ySingleUserValueFromJSON(value)]);
  }

  delete(index: number): void {
    this.valueElement.valueRequired.delete(index);
  }

  reorder(index: number, newIndex: number): void {
    const val = this.valueElement.valueRequired.get(index).toJSON();
    this.valueElement.valueRequired.delete(index);
    this.valueElement.valueRequired.insert(newIndex, [ySingleUserValueFromJSON(val)]);
  }

  setTo(values: SingleUserValue[]) {
    const valueRef = new Y.Array();
    this.input.set(ValueProperties.value, valueRef);
    this.valueElement = this.createValueElement();

    this.valueElement.valueRequired.push(values.map((v) => ySingleUserValueFromJSON(v)));
  }

  toJSON(): ValueJSON<FieldType.User, SingleUserValue[]> {
    return {
      type: this.type,
      value: this.value.map((v) => v.toJSON())
    };
  }

  static fromJSON(json: ValueJSON<FieldType.User, SingleUserValue[]>) {
    return new YUserValue(
      new Y.Map([
        [ValueProperties.type, FieldMapping[json.type]],
        [
          ValueProperties.value,
          Y.Array.from(json.value.map((v) => ySingleUserValueFromJSON(v).unwrap()))
        ]
      ])
    );
  }

  unwrap(): Y.AbstractType<any> {
    return this.input;
  }

  dispose(): void {
    this.valueElement.dispose();
  }
}

export class YUserValueWithoutCurrent
  implements UserValue, MutableUserValue, WrappedYType, DisposableElement
{
  private valueElement: SynchronizedMapElement<SynchronizedArray<YReferencedUserValue>>;

  constructor(
    private input: Y.Map<any>,
    private getCurrentUser: () => ReferencedUserValue
  ) {
    this.valueElement = this.createValueElement();
  }

  private createValueElement() {
    return new SynchronizedMapElement<SynchronizedArray<YReferencedUserValue>>(
      this.input,
      ValueProperties.value,
      (yarray) => {
        if (yarray instanceof Y.Array) {
          return new SynchronizedArray(yarray as Y.Array<unknown>, (value: unknown) => {
            if (value instanceof Y.Map) {
              const type = value.get(UserValueProperties.type);
              if (type === YUserValueTypes.YReferencedUser) {
                return new YReferencedUserValue(value);
              }
            }
          });
        }
      },
      () => new Y.Array()
    );
  }

  get isEmpty() {
    return !this.valueElement.value?.toArray().length;
  }

  get type(): FieldType.User {
    return FieldType.User;
  }

  get value() {
    return this.valueElement.value?.toArray() ?? [];
  }

  add(value: SingleUserValue): void {
    this.valueElement.valueRequired.push([
      YReferencedUserValue.fromJson(
        value.type === UserType.Current ? this.getCurrentUser() : value
      )
    ]);
  }

  insert(idx: number, value: SingleUserValue): void {
    this.valueElement.valueRequired.insert(idx, [
      YReferencedUserValue.fromJson(
        value.type === UserType.Current ? this.getCurrentUser() : value
      )
    ]);
  }

  delete(index: number): void {
    this.valueElement.valueRequired.delete(index);
  }

  reorder(index: number, newIndex: number): void {
    const val = this.valueElement.valueRequired.get(index).toJSON();
    this.valueElement.valueRequired.delete(index);
    this.valueElement.valueRequired.insert(newIndex, [
      YReferencedUserValue.fromJson(
        val.type === UserType.Current ? this.getCurrentUser() : val
      )
    ]);
  }

  setTo(values: SingleUserValue[]) {
    const valueRef = new Y.Array();
    this.input.set(ValueProperties.value, valueRef);
    this.valueElement = this.createValueElement();

    this.valueElement.valueRequired.push(
      values.map((v) =>
        YReferencedUserValue.fromJson(
          v.type === UserType.Current ? this.getCurrentUser() : v
        )
      )
    );
  }

  toJSON(): ValueJSON<FieldType.User, SingleUserValue[]> {
    return {
      type: this.type,
      value: this.value.map((v) => v.toJSON())
    };
  }

  static fromJSON(
    json: ValueJSON<FieldType.User, SingleUserValue[]>,
    getCurrentUser: GetCurrentUser
  ) {
    return new YUserValue(
      new Y.Map([
        [ValueProperties.type, FieldMapping[json.type]],
        [
          ValueProperties.value,
          Y.Array.from(
            json.value.map((v) =>
              v.type === UserType.Current
                ? getCurrentUser()
                : ySingleUserValueFromJSON(v).unwrap()
            )
          )
        ]
      ])
    );
  }

  unwrap(): Y.AbstractType<any> {
    return this.input;
  }

  dispose(): void {
    this.valueElement.dispose();
  }
}
